/**
 * Utility functions for project handling and display
 */

/**
 * Sort projects in a consistent order
 * @param {Array} projects - Array of project objects
 * @param {Object} options - Sorting options
 * @returns {Array} Sorted projects array
 */
export const sortProjects = (projects, options = {}) => {
  const {
    sortBy = 'number_then_disease',
    direction = 'asc',
    prioritizeRecent = false
  } = options;

  return [...projects].sort((a, b) => {
    // First, handle recent projects if prioritized
    if (prioritizeRecent) {
      const aRecent = isRecentProject(a);
      const bRecent = isRecentProject(b);
      if (aRecent && !bRecent) return -1;
      if (!aRecent && bRecent) return 1;
    }

    let comparison = 0;

    switch (sortBy) {
      case 'number_then_disease':
        comparison = sortByNumberThenDisease(a, b);
        break;
      case 'disease_then_number':
        comparison = sortByDiseaseThenNumber(a, b);
        break;
      case 'pi_name':
        comparison = sortByPIName(a, b);
        break;
      case 'date_created':
        comparison = sortByDateCreated(a, b);
        break;
      case 'date_received':
        comparison = sortByDateReceived(a, b);
        break;
      default:
        comparison = sortByNumberThenDisease(a, b);
    }

    return direction === 'desc' ? -comparison : comparison;
  });
};

/**
 * Check if a project is considered recent (created/updated within last 30 days)
 */
const isRecentProject = (project) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const createdDate = new Date(project.created_at);
  const updatedDate = new Date(project.updated_at);
  
  return createdDate > thirtyDaysAgo || updatedDate > thirtyDaysAgo;
};

/**
 * Sort by project number first, then by disease
 */
const sortByNumberThenDisease = (a, b) => {
  // Projects with numbers come first - handle both numeric and string values
  const aHasNumber = a.project_number !== null && a.project_number !== undefined && String(a.project_number).trim() !== '';
  const bHasNumber = b.project_number !== null && b.project_number !== undefined && String(b.project_number).trim() !== '';
  
  if (aHasNumber && !bHasNumber) return -1;
  if (!aHasNumber && bHasNumber) return 1;
  
  // If both have numbers, sort by project number
  if (aHasNumber && bHasNumber) {
    const aNum = extractNumber(a.project_number);
    const bNum = extractNumber(b.project_number);
    
    if (aNum !== null && bNum !== null) {
      return aNum - bNum;
    }
    
    // Fallback to string comparison
    return String(a.project_number).localeCompare(String(b.project_number));
  }
  
  // If neither has numbers, sort by disease name
  const aDisease = a.disease || 'Unnamed Project';
  const bDisease = b.disease || 'Unnamed Project';
  return aDisease.localeCompare(bDisease);
};

/**
 * Sort by disease first, then by project number
 */
const sortByDiseaseThenNumber = (a, b) => {
  const aDisease = a.disease || 'Unnamed Project';
  const bDisease = b.disease || 'Unnamed Project';
  
  const diseaseComparison = aDisease.localeCompare(bDisease);
  if (diseaseComparison !== 0) return diseaseComparison;
  
  // If diseases are the same, sort by project number
  const aNum = extractNumber(a.project_number);
  const bNum = extractNumber(b.project_number);
  
  if (aNum !== null && bNum !== null) {
    return aNum - bNum;
  }
  
  return String(a.project_number || '').localeCompare(String(b.project_number || ''));
};

/**
 * Sort by PI name
 */
const sortByPIName = (a, b) => {
  const aPI = a.pi_name || '';
  const bPI = b.pi_name || '';
  return aPI.localeCompare(bPI);
};

/**
 * Sort by date created
 */
const sortByDateCreated = (a, b) => {
  const aDate = new Date(a.created_at);
  const bDate = new Date(b.created_at);
  return bDate - aDate; // Most recent first
};

/**
 * Sort by date received
 */
const sortByDateReceived = (a, b) => {
  const aDate = new Date(a.date_received);
  const bDate = new Date(b.date_received);
  return bDate - aDate; // Most recent first
};

/**
 * Extract numeric value from project number (handles both numeric and string values)
 */
const extractNumber = (projectNumber) => {
  if (projectNumber === null || projectNumber === undefined) return null;
  
  // If it's already a number, return it
  if (typeof projectNumber === 'number') {
    return projectNumber;
  }
  
  // If it's a string, extract the first number
  const match = String(projectNumber).match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
};

/**
 * Get project status based on various criteria
 */
export const getProjectStatus = (project) => {
  const now = new Date();
  const createdDate = new Date(project.created_at);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  if (createdDate > thirtyDaysAgo) {
    return 'new';
  }
  
  // Could add more status logic here based on other criteria
  return 'active';
};

/**
 * Validate project data for display
 */
export const validateProjectForDisplay = (project) => {
  const issues = [];
  
  if (!project.disease || project.disease.trim() === '') {
    issues.push('Missing disease/condition');
  }
  
  if (!project.pi_name || project.pi_name.trim() === '') {
    issues.push('Missing PI name');
  }
  
  if (!project.pi_institute || project.pi_institute.trim() === '') {
    issues.push('Missing PI institute');
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
};